<template lang="pug">
    el-dialog.currency-form-dialog(
        v-if="isQuickCreate"
        :title="title"
        :visible.sync="visible"
        :before-close="handleBeforeClose"
        :close-on-click-modal="false"
        :append-to-body="true"
        top="10vh"
        width="420px"
        @closed="handleClosed"
    )
        el-form(
            ref="form"
            :model="form"
            :rules="rules"
            :validate-on-rule-change="false"
            label-position="top"
            @submit.native.prevent="submit"
        )
            el-form-item(:label="$t('ISO 4217 code')" prop="iso4217")
                el-select(
                    v-model="form.iso4217"
                    filterable
                    :placeholder="$t('Select a currency')"
                    :no-match-text="$t('No currencies found')"
                    @change="handleIsoChange"
                )
                    el-option(
                        v-for="option in isoOptions"
                        :key="option.code"
                        :label="`${option.code} — ${option.name}`"
                        :value="option.code"
                    )
            el-form-item(:label="$t('Sign / Symbol')" prop="sign")
                el-input(v-model="form.sign" :maxlength="8")
        template(slot="footer")
            .dialog-footer
                .footer-left
                    el-button(
                        size="mini"
                        icon="el-icon-fa-times fas"
                        :disabled="saving"
                        @click="closeDrawer"
                    ) {{ $t('Dismiss') }}
                .footer-right
                    el-button(
                        size="mini"
                        type="primary"
                        icon="el-icon-fa-check fas"
                        :loading="saving"
                        @click="submit"
                    ) {{ $t('Save') }}

    el-drawer.currency-form-drawer(
        v-else
        :title="title"
        :visible.sync="visible"
        :before-close="handleBeforeClose"
        :wrapper-closable="false"
        :modal="true"
        :append-to-body="true"
        :size="size"
        @closed="handleClosed"
    )
        .drawer-body
            el-form(
                ref="form"
                :model="form"
                :rules="rules"
                label-position="top"
                @submit.native.prevent="submit"
            )
                el-form-item(:label="$t('ISO 4217 code')" prop="iso4217")
                    el-select(
                        v-model="form.iso4217"
                        filterable
                        :placeholder="$t('Select a currency')"
                        :no-match-text="$t('No currencies found')"
                        @change="handleIsoChange"
                    )
                        el-option(
                            v-for="option in isoOptions"
                            :key="option.code"
                            :label="`${option.code} — ${option.name}`"
                            :value="option.code"
                        )
                el-form-item(:label="$t('Sign / Symbol')" prop="sign")
                    el-input(v-model="form.sign" :maxlength="8")
        .drawer-footer
            .footer-left
                el-button(
                    round
                    size="mini"
                    icon="el-icon-fa-times fas"
                    :disabled="saving"
                    @click="closeDrawer"
                ) {{ $t('Dismiss') }}
            .footer-right
                el-button(
                    v-if="form.id"
                    round
                    size="mini"
                    type="danger"
                    icon="el-icon-fa-trash-alt fas"
                    :disabled="saving"
                    @click="$emit('delete', form.id)"
                ) {{ $t('Delete') }}
                el-button(
                    round
                    size="mini"
                    type="primary"
                    icon="el-icon-fa-check fas"
                    :loading="saving"
                    @click="submit"
                ) {{ $t('Save') }}
</template>

<i18n>
{
    "de": {
        "ISO 4217 code": "ISO 4217 Code",
        "Sign / Symbol": "Zeichen / Symbol",
        "Select a currency": "Währung auswählen",
        "No currencies found": "Keine Währungen gefunden",
        "New Currency": "Neue Währung",
        "Edit Currency": "Währung bearbeiten",
        "Save": "Speichern",
        "Dismiss": "Abbrechen",
        "Delete": "Löschen"
    }
}
</i18n>

<script>
import { getAllCurrencies, getCurrencyByCode } from '@/utils/currencies';
import discardGuard from '@/mixins/discardGuard';

export default {
    name: 'CurrencyForm',
    mixins: [discardGuard],
    props: {
        visibility: {
            type: Boolean,
            default: false,
        },
        data: {
            type: Object,
            default: () => ({}),
        },
        saving: {
            type: Boolean,
            default: false,
        },
        size: {
            type: String,
            default: '420px',
        },
        appendToBody: {
            type: Boolean,
            default: false,
        },
        existingIsoCodes: {
            type: Array,
            default: () => [],
        },
        isQuickCreate: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            form: this.buildForm(this.data),
            formSnapshot: undefined,
            rules: {
                iso4217: 'required|length:3',
                sign: 'required',
            },
        };
    },
    computed: {
        visible: {
            get() {
                return this.visibility;
            },
            set(value) {
                this.$emit('update:visibility', value);
            },
        },
        title() {
            return this.form.id ? this.$t('Edit Currency') : this.$t('New Currency');
        },
        isoOptions() {
            const blocked = (this.existingIsoCodes || []).filter((code) => code !== this.form.iso4217);
            return getAllCurrencies()
                .filter((entry) => !blocked.includes(entry.code))
                .map((entry) => ({ code: entry.code, name: entry.name }));
        },
    },
    watch: {
        data: {
            handler(value) {
                this.form = this.buildForm(value);
            },
            deep: true,
        },
        visibility(value) {
            if (value) {
                this.form = this.buildForm(this.data);
                this.formSnapshot = this.snapshotOf(this.form);
            }
        },
    },
    methods: {
        buildForm(source) {
            return {
                id: source && source.id ? source.id : null,
                iso4217: source && source.iso4217 ? source.iso4217 : '',
                sign: source && source.sign ? source.sign : '',
            };
        },
        handleIsoChange(code) {
            const meta = getCurrencyByCode(code);
            if (meta && (!this.form.sign || this.isPreviousAutoSign())) {
                this.form.sign = meta.symbol;
            }
        },
        isPreviousAutoSign() {
            const previousMeta = this.form.iso4217 && getCurrencyByCode(this.form.iso4217);
            return previousMeta ? previousMeta.symbol === this.form.sign : true;
        },
        submit() {
            this.$refs.form.validate((valid) => {
                if (!valid) return;
                this.$emit('submit', { ...this.form });
            });
        },
        closeDrawer() {
            this.visible = false;
        },
        handleBeforeClose(done) {
            this.confirmDiscard(() => this.changedSince(this.formSnapshot, this.form))(done);
        },
        handleClosed() {
            if (this.$refs.form) this.$refs.form.clearValidate();
            this.form = this.buildForm({});
            this.$emit('closed');
        },
    },
};
</script>

<style lang="sass" scoped>
.currency-form-drawer
    ::v-deep .el-drawer
        display: flex
        flex-direction: column
        .drawer-body
            flex-grow: 1
            padding: 1rem
            overflow: auto
            .el-select
                width: 100%
        .drawer-footer
            display: flex
            justify-content: space-between
            align-items: center
            padding: 12px 20px
            border-top: 1px solid #e4e7ed
            .footer-left,
            .footer-right
                display: flex
                gap: 8px

.currency-form-dialog
    ::v-deep .el-dialog__body
        .el-select
            width: 100%
    ::v-deep .el-dialog__footer
        padding: 12px 20px
        .dialog-footer
            display: flex
            justify-content: space-between
            align-items: center
            .footer-left,
            .footer-right
                display: flex
                gap: 8px
</style>
